<script setup lang="ts">
import type { DropdownOption, DropdownValue } from '~/types/ui'

const props = withDefaults(
  defineProps<{
    modelValue: DropdownValue | DropdownValue[]
    options: DropdownOption[]
    label: string
    placeholder?: string
    icon?: string
    multiple?: boolean
    showArrow?: boolean
    variant?: 'plain' | 'input'
    align?: 'start' | 'end'
    avoidHeader?: boolean
  }>(),
  { placeholder: '请选择', showArrow: true, variant: 'plain', align: 'end', avoidHeader: true }
)
const emit = defineEmits<{
  'update:modelValue': [value: DropdownValue | DropdownValue[]]
}>()
const selectedValues = computed(() => Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue])
const selectedLabel = computed(() => props.options
  .filter(option => selectedValues.value.includes(option.value))
  .map(option => option.label).join('、') || props.placeholder
)

// selectOption 更新单选或多选值
const selectOption = (value: DropdownValue, close: () => void) => {
  if (!props.multiple) {
    emit('update:modelValue', value)
    close()
    return
  }
  const values = selectedValues.value
  const next = values.includes(value) ? values.filter(item => item !== value) : [...values, value]
  emit('update:modelValue', next)
}
</script>

<template>
  <BlogPopupMenu class="dropdown" :class="{ 'dropdown-input': variant === 'input' }" :label="label" :align="align" :avoid-header="avoidHeader">
    <template #trigger="{ open, toggle, menuId }">
      <button
        type="button"
        class="dropdown-trigger"
        :aria-label="label"
        aria-haspopup="menu"
        :aria-controls="menuId"
        :aria-expanded="open"
        @click="toggle"
      >
        <i v-if="icon" :class="icon" aria-hidden="true" />
        <span class="dropdown-label">{{ selectedLabel }}</span>
        <i v-if="showArrow" class="fa-solid fa-chevron-down" :class="{ expanded: open }" aria-hidden="true" />
      </button>
    </template>
    <template #default="{ close }">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="dropdown-menu-item"
        :role="multiple ? 'menuitemcheckbox' : 'menuitemradio'"
        :aria-checked="selectedValues.includes(option.value)"
        :disabled="option.disabled"
        :class="{ 'is-selected': selectedValues.includes(option.value) }"
        :style="{ '--dropdown-item-depth': option.depth || 0 }"
        @click="selectOption(option.value, close)"
      >
        <i v-if="option.icon" :class="option.icon" aria-hidden="true" />
        <span class="dropdown-label">{{ option.label }}</span>
        <i v-if="multiple && selectedValues.includes(option.value)" class="fa-solid fa-check dropdown-check" aria-hidden="true" />
      </button>
    </template>
  </BlogPopupMenu>
</template>

<style scoped>
.dropdown {
  display: inline-block;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 0.4em;
  max-width: 100%;
  padding: var(--dropdown-padding, 0.3em 0);
  border: 0;
  background: transparent;
  color: var(--text-soft);
  font: inherit;
  cursor: pointer;
  transition: color 0.2s ease;
}

.dropdown-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-trigger > i {
  flex-shrink: 0;
}

.dropdown-trigger:hover,
.dropdown-trigger:focus-visible {
  color: var(--brand);
}

.dropdown-input {
  width: 100%;
}

.dropdown-input .dropdown-trigger {
  width: 100%;
  min-height: var(--dropdown-height, 40px);
  padding: var(--dropdown-padding, 10px);
  border: var(--dropdown-border, 1px solid var(--line));
  border-radius: var(--dropdown-radius, 10px);
  background: var(--editor-bg);
  color: var(--text);
}

.dropdown-input .dropdown-trigger:focus-visible {
  box-shadow: var(--dropdown-focus-shadow, none);
}

.dropdown-input .dropdown-label {
  flex: 1;
  text-align: start;
}

.fa-chevron-down {
  margin-left: auto;
  font-size: 0.65em;
  transition: transform 0.2s ease;
}

.fa-chevron-down.expanded {
  transform: rotate(180deg);
}

.dropdown-check {
  margin-left: auto;
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-trigger,
  .fa-chevron-down {
    transition: none;
  }
}
</style>
